
const MediaGallery = class extends HTMLElement {
    constructor() {
        super();
        this.section = this.closest(".js-product");
        this.mainImageContainer = this.querySelector(".main-image");
        this.slideshow = this.mainImageContainer.querySelector(
            ".main-image carousel-slider"
        );
        this.collage = this.querySelector(".product-media-collage");
        this.xrButton = this.querySelector("[data-shopify-xr]");
        this.variantPicker = this.section.querySelector("variant-picker");
        this.mediaGroupingEnabled =
            this.variantPicker &&
            this.hasAttribute("data-media-grouping-enabled") &&
            this.getMediaGroupData();
        this.mediaLists = [
            this.querySelectorAll(".main-image .slider__item"),
            this.querySelectorAll(".product-media-collage__item"),
            this.querySelectorAll(".thumbnails .slider__item"),
        ];

        if (this.variantPicker) {
            const toLoad = [this.variantPicker];
            if (this.slideshow.offsetParent) {
                toLoad.push(this.slideshow);
            }
            const thumbnailSlider = this.querySelector("carousel-slider.thumbnails");
            if (thumbnailSlider && thumbnailSlider.offsetParent) {
                toLoad.push(thumbnailSlider);
            }
            theme.whenComponentLoaded(toLoad, this.setFromVariantPicker.bind(this));
        }

        if (this.hasAttribute("data-zoom-enabled")) {
            this.galleryModal = this.querySelector(
                ".js-media-zoom-template"
            ).content.firstElementChild.cloneNode(true);
            this.mediaLists.push(
                this.galleryModal.querySelectorAll(".gallery-viewer__thumb")
            );
            theme.addDelegateEventListener(
                this,
                "click",
                ".show-gallery",
                this.openGalleryViewer.bind(this)
            );

            if (this.hasAttribute("data-zoom-preload")) {
                this.mainImageContainer.addEventListener(
                    "mouseover",
                    MediaGallery.hoverMainImage.bind(this)
                );
                this.mainImageContainer.addEventListener(
                    "touchstart",
                    MediaGallery.hoverMainImage.bind(this)
                );
            }
        }

        this.section.addEventListener(
            "on:carousel-slider:select",
            this.selectMainMedia.bind(this)
        );
        theme.addDelegateEventListener(
            this,
            "click",
            ".thumbnail",
            this.selectThumbnail.bind(this)
        );
        this.section.addEventListener(
            "on:variant:change",
            this.onVariantChange.bind(this)
        );

        // preload slideshow images after a short delay, if slideshow is visible
        setTimeout(() => {
            if (this.slideshow.offsetParent) {
                this.slideshow.querySelectorAll(".theme-img").forEach((el) => {
                    el.loading = "eager";
                });
            }
        }, 3000);
    }

    setFromVariantPicker() {
        if (this.mediaGroupingEnabled) {
            this.setMediaGroupOnFirstLoad();
        } else {
            // Find which variant is selected and set active media.
            const variant = this.variantPicker.getSelectedVariant();
            if (variant && variant.featured_media) {
                this.setActiveMedia(variant.featured_media.id, true, true);
            }
        }
    }

    /**
     * Run on first load to set initial media group and focussed image
     */
    setMediaGroupOnFirstLoad() {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());

        // Find which variant is selected and set active media.
        const variant = this.variantPicker.getSelectedVariant();
        if (variant && variant.featured_media) {
            this.setActiveMedia(variant.featured_media.id, true, true);
            return;
        }

        // Default to first visible image.
        this.setActiveMedia(
            this.querySelector(".thumbnails .slider__item:not([hidden])").dataset
                .mediaId,
            false,
            true
        );
    }

    /**
     * Hover main image.
     * @param {Event} evt - hover event for image
     */
    static hoverMainImage(evt) {
        MediaGallery.loadImage(
            evt.currentTarget.querySelector(".slider__item.is-active a.show-gallery")
        );
    }

    /**
     * Open large image viewer.
     * @param {Event} evt - click event for image
     * @param {Element} imageLink - link clicked on
     */
    openGalleryViewer(evt, imageLink) {
        evt.preventDefault();

        // add to document on first open
        if (!this.galleryModal.parentElement) {
            document.body.appendChild(this.galleryModal);
        }

        this.galleryModal.open(imageLink);
        const viewer = this.galleryModal.querySelector("gallery-viewer");
        viewer.init(imageLink.getAttribute("href"));
        viewer.focus();

        if (this.hasAttribute("data-zoom-preload")) {
            this.mediaLists[0].forEach((sliderItem) => {
                MediaGallery.loadImage(sliderItem.querySelector("a.show-gallery"));
            });
        }
    }

    /**
     * Load an image.
     * @param {Element} anchor - anchor element
     */
    static loadImage(anchor) {
        if (
            anchor.hasAttribute("data-loading") ||
            anchor.hasAttribute("data-loaded")
        )
            return;

        anchor.setAttribute("data-loading", "");

        const image = new Image(5000);
        image.addEventListener(
            "load",
            () => {
                anchor.removeAttribute("data-loading");
                anchor.setAttribute("data-loaded", "");
            },
            {
                once: true,
            }
        );
        image.src = anchor.href;
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
        if (this.mediaGroupingEnabled) {
            this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors(evt));
        }

        if (evt.detail.variant && evt.detail.variant.featured_media) {
            this.setActiveMedia(evt.detail.variant.featured_media.id, true);
        } else if (this.mediaGroupChanged) {
            // default to first visible image, if group changed and current variant has no related image
            this.setActiveMedia(
                this.querySelector(".thumbnails .slider__item:not([hidden])").dataset
                    .mediaId,
                true
            );
        }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @param {Event} evt - variant change event dispatched by variant-picker
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors(evt) {
        if (evt) {
            return evt.detail.selectedOptions[
                this.getMediaGroupData().groupOptionIndex
                ];
        }

        return this.variantPicker.getSelectedOptions()[
            this.getMediaGroupData().groupOptionIndex
            ];
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
        if (typeof this.variantMediaData === "undefined") {
            const dataEl = this.querySelector(".js-data-variant-media");
            if (dataEl) {
                this.variantMediaData = JSON.parse(dataEl.textContent);
            } else {
                this.variantMediaData = false;
            }
        }

        return this.variantMediaData;
    }

    /**
     * Show only images associated to the current variant.
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
        this.mediaGroupChanged = this.currentMediaGroup !== groupName;
        this.currentMediaGroup = groupName;

        if (!this.mediaGroupChanged) return;

        if (!groupName) {
            this.mediaLists.forEach((list) => {
                list.forEach((mediaItem) => {
                    mediaItem.hidden = false;
                });
            });
            return;
        }

        const mediaGroupData = this.getMediaGroupData();

        this.mediaLists.forEach((list) => {
            // Set hidden
            list.forEach((el) => {
                const mediaItemGroup = mediaGroupData.media[el.dataset.mediaId].group;
                const showThis =
                    mediaItemGroup === groupName || mediaItemGroup === true;
                el.hidden = !showThis;
            });

            // Move all-groups media to start/end
            [...list].forEach((el) => {
                if (mediaGroupData.media[el.dataset.mediaId].group === true) {
                    if (mediaGroupData.media[el.dataset.mediaId].position === "start") {
                        el.parentElement.prepend(el);
                    } else {
                        el.parentElement.append(el);
                    }
                }
            });

            // Move hidden elements to the end (to allow nth-child CSS in layouts)
            [...list]
                .filter((el) => el.hidden)
                .forEach((el) => el.parentElement.append(el));

            // Refresh slider
            if (list.length > 0) {
                const slider = list[0].closest("carousel-slider");
                if (slider && slider.offsetParent) {
                    slider.refresh();
                }
            }
        });
    }

    /**
     * Sets the active media item.
     * @param {number} mediaId - Media ID to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     * @param {boolean} [firstLoad=false] - Is gallery initialising.
     */
    setActiveMedia(mediaId, scrollToItem = true, firstLoad = false) {
        const strMediaId = mediaId.toString();
        const mediaEl = this.mainImageContainer.querySelector(
            `[data-media-id="${strMediaId}"]`
        );

        if (scrollToItem) {
            // slideshow (if visible)
            if (
                this.slideshow.hasAttribute("loaded") &&
                this.slideshow.offsetParent
            ) {
                this.slideshow.scrollToElement(mediaEl, "instant");
            }

            // collage (if visible and not first load)
            if (this.collage && this.collage.offsetParent && !firstLoad) {
                const collageMediaEl = this.collage.querySelector(
                    `[data-media-id="${strMediaId}"]`
                );
                if (collageMediaEl) {
                    theme.scrollToRevealElement(collageMediaEl);

                    clearTimeout(this.collage.dataset.highlightTimeoutId);
                    // When media group changes, assume first media has attention
                    if (this.mediaGroupChanged) {
                        this.collage
                            .querySelectorAll(".product-media-collage__item--highlight-off")
                            .forEach((el) => {
                                el.classList.remove(
                                    "product-media-collage__item--highlight-off"
                                );
                            });
                    } else {
                        this.collage
                            .querySelectorAll(".product-media-collage__item")
                            .forEach((el) => {
                                if (el === collageMediaEl) {
                                    el.classList.remove(
                                        "product-media-collage__item--highlight-off"
                                    );
                                } else {
                                    el.classList.add(
                                        "product-media-collage__item--highlight-off"
                                    );
                                }
                            });
                        this.collage.dataset.highlightTimeoutId = setTimeout(() => {
                            this.collage
                                .querySelectorAll(".product-media-collage__item--highlight-off")
                                .forEach((el) => {
                                    el.classList.remove(
                                        "product-media-collage__item--highlight-off"
                                    );
                                });
                        }, 1200);
                    }
                }
            }
        }

        this.mediaLists.forEach((list) => {
            list.forEach((mediaItem, index) => {
                if (mediaItem.dataset.mediaId === strMediaId) {
                    mediaItem.classList.add("is-active");
                    const carousel = mediaItem.closest("carousel-slider");
                    if (carousel && carousel.offsetParent) {
                        carousel.scrollToElement(mediaItem);
                    }

                    // dots
                    this.querySelectorAll(".product-block__image-dot").forEach(
                        (el, iter) => {
                            el.classList.toggle(
                                "product-block__image-dot--active",
                                iter === index
                            );
                        }
                    );
                } else {
                    mediaItem.classList.remove("is-active");
                }
            });
        });

        // play media
        window.pauseAllMedia();
        if (mediaEl) {
            // deferred
            const deferredMedia = mediaEl.querySelector(
                "deferred-media, product-model"
            );
            if (deferredMedia) deferredMedia.loadContent(true);

            // video tag
            const playableMedia = mediaEl.querySelector("video");
            if (playableMedia) {
                playableMedia.play();
            }
        }

        // set model ID
        if (this.xrButton && this.mediaIsModel(mediaId)) {
            this.xrButton.dataset.shopifyModel3dId = mediaId;
        }

        document.dispatchEvent(
            new CustomEvent("on:media-gallery:change", {
                bubbles: true,
                cancelable: false,
            })
        );
    }

    /**
     * Check if media is a 3D model
     * @param {number} mediaId - Media ID to check.
     * @returns {boolean} - If it is a 3D model.
     */
    mediaIsModel(mediaId) {
        return !!this.querySelector(
            `.product-media--model[data-model-id="${mediaId}"]`
        );
    }

    selectThumbnail(evt, thumbnail) {
        evt.preventDefault();
        this.setActiveMedia(thumbnail.parentNode.dataset.mediaId, true);
    }

    selectMainMedia(evt) {
        if (evt.detail.slide.dataset.mediaId) {
            this.setActiveMedia(evt.detail.slide.dataset.mediaId, false);
        }
    }
};

window.customElements.define("media-gallery", MediaGallery);